'use client';

import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
export interface PartnerPriceInfo {
  /** Public retail price at qty 1 (special-aware). */
  retailPrice: string;
  /** Group-discounted price for the logged-in customer. */
  partnerPrice: string;
  /** Customer group display name (e.g. "Gold Partner"). */
  groupName: string | null;
}

/**
 * Customer-group (partner/installer) price for a product.
 *
 * Product pages are static/ISR, so group pricing can't render server-side.
 * When the visitor is logged in, this fetches their price from
 * /api/products/[slug]/price and returns it only when a real discount applies.
 * Fail-open: anonymous visitors, customers without a group discount, and any
 * fetch error all return null — the PDP falls back to the public price.
 */
export function usePartnerPrice(productSlug: string): PartnerPriceInfo | null {
  const {
    status
  } = useSession();
  // Key the stored price to the slug it was fetched for. On client-side
  // navigation the page component instance is preserved (same catch-all
  // route), so a slug-agnostic state would leak the previous product's
  // partner price into the next product while its fetch fails, returns no
  // discount, or is still in flight.
  const [state, setState] = useState<{
    slug: string;
    info: PartnerPriceInfo;
  } | null>(null);
  useEffect(() => {
    if (status !== 'authenticated') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/products/${encodeURIComponent(productSlug)}/price`);
        if (!res.ok) return;
        const json = (await res.json()) as {
          data?: {
            retailPrice?: string;
            partnerPrice?: string | null;
            groupName?: string | null;
          };
        };
        const data = json?.data;
        if (!cancelled && data?.partnerPrice && data.retailPrice) {
          setState({
            slug: productSlug,
            info: {
              retailPrice: data.retailPrice,
              partnerPrice: data.partnerPrice,
              groupName: data.groupName ?? null
            }
          });
        }
      } catch {
        // Fail-open: keep showing the public price.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productSlug, status]);

  // Derive the return value: only surface the stored price when it belongs to
  // the current slug and the visitor is still authenticated. A stale price
  // from a previously viewed product can never bleed through, since its slug
  // won't match.
  return status === 'authenticated' && state?.slug === productSlug ? state.info : null;
}